/**
 * This module is the entry point for the VariantReader module.
 * It exports the VariantReader class that manages component variants.
 */
import { useState, useEffect, useMemo } from 'react';
import { GetCMSContent } from 'aldoo-ra/CMS/api';
import PubSub from 'pubsub-js';
import { useVariantContext } from './VariantContext.jsx';
import { TailwindParser } from '../aldoo-vbecs/utils/TailwindParser.js';

// Use Vite's glob import for variants (lazy load)
const defaultVariants = import.meta.glob('/aldoo-ra/*/variants.jsx', { eager: true });
const userVariants = import.meta.glob('/components/Variants/*.jsx', { eager: true });


// Helper to normalize paths to component names
const getComponentName = (path) => {
    if (path.includes('/Variants/')) {
        return path.split('/Variants/')[1].replace('.jsx', '');
    }
    return path.split('/').slice(-2)[0];
};

// Deep merge helper function
const deepMergeVariants = (defaultValues, userValues) => {
    // Handle null/undefined cases
    if (!defaultValues) return userValues;
    if (!userValues) return defaultValues;

    // Create a new object to store merged results
    const merged = { ...defaultValues };

    // Iterate through user values
    Object.keys(userValues).forEach(key => {
        if (userValues[key] && typeof userValues[key] === 'object' && !Array.isArray(userValues[key])) {
            // If both values are objects, recursively merge them
            if (defaultValues[key] && typeof defaultValues[key] === 'object' && !Array.isArray(defaultValues[key])) {
                merged[key] = deepMergeVariants(defaultValues[key], userValues[key]);
            } else {
                // If default value isn't an object or doesn't exist, use user value
                merged[key] = userValues[key];
            }
        } else {
            // For non-objects (including arrays), use user value
            merged[key] = userValues[key];
        }
    });

    return merged;
};

// Create maps of variant loaders
const defaultVariantMap = Object.entries(defaultVariants).reduce((acc, [path, module]) => {
    const componentName = getComponentName(path);
    acc[componentName] = module.default;
    return acc;
}, {});

const userVariantMap = Object.entries(userVariants).reduce((acc, [path, module]) => {
    const componentName = getComponentName(path);
    acc[componentName] = module.default;
    return acc;
}, {});

export class VariantReader {
    static #variantCache = new Map();
    static #pendingRequests = new Map();

    /**
     * React hook for using variant class strings (with state prefixes intact)
     * @param {string} componentName - Name of the component
     * @param {string} variant - Name of the variant
     * @returns {string} Variant className string with state prefixes (e.g., "hover:bg-red-500")
     */
    static useVariantClassString({
        componentName,
        variant = 'default'
    }) {
        const ssrVariants = useVariantContext();
        const [asyncVariants, setAsyncVariants] = useState(null);

        // Compute variant synchronously on every render (useMemo ensures it only recomputes when deps change)
        const variantClass = useMemo(() => {
            let variants = null;

            // 1. Runtime cache (API updates / editor state) - HIGHEST PRIORITY
            // This takes precedence because it's updated immediately when variants are edited
            if (this.#variantCache.has(componentName)) {
                variants = this.#variantCache.get(componentName);
            }
            // 2. Check async fetched variants (from CMS, updated by refresh)
            else if (asyncVariants) {
                variants = asyncVariants;
            }
            // 3. SSR context (for initial hydration)
            else if (ssrVariants && ssrVariants[componentName]) {
                variants = ssrVariants[componentName];
            }
            // 4. Window cache (synced from editor for live preview)
            else if (typeof window !== 'undefined' && window.__VBECS_VARIANT_CACHE__?.[componentName]) {
                variants = window.__VBECS_VARIANT_CACHE__[componentName];
                this.#variantCache.set(componentName, variants);
            }
            // 5. Vike pageContext (client-only hydration fallback)
            else if (typeof window !== 'undefined' && window.__VIKE_PAGECONTEXT__?.pageContext?.ssrVariants?.[componentName]) {
                variants = window.__VIKE_PAGECONTEXT__.pageContext.ssrVariants[componentName];
            }
            // 6. Manual SSR variants (legacy hydration)
            else if (typeof window !== 'undefined' && window.__SSR_VARIANTS__?.[componentName]) {
                variants = window.__SSR_VARIANTS__[componentName];
            }

            // 7. Synchronous File Fallback (eager loaded)
            if (!variants && (defaultVariantMap[componentName] || userVariantMap[componentName])) {
                 variants = deepMergeVariants(
                     defaultVariantMap[componentName] || {},
                     userVariantMap[componentName] || {}
                 );
            }

            let result = '';
            if (variants) {
                if (variant && variant.includes(' ')) {
                    const keys = variant.split(' ').filter(Boolean);
                    const classes = keys.map(k => variants[k] || '').join(' ');
                    result = classes || variants.default || '';
                } else {
                    result = variants[variant] || variants.default || '';
                }
            }

            return result;
        }, [componentName, variant, ssrVariants, asyncVariants]);

        // Fetch async variants from CMS in background
        useEffect(() => {
            this.readVariants(componentName).then((variants) => {
                setAsyncVariants(variants);
            });

            const token = PubSub.subscribe('VARIANTS_UPDATED', () => {
                this.readVariants(componentName).then((variants) => {
                    setAsyncVariants(variants);
                });
            });

            return () => {
                PubSub.unsubscribe(token);
            };
        }, [componentName]);

        return variantClass || '';
    }

    /**
     * React hook for using variants in components
     * @param {string} componentName - Name of the component
     * @param {string} variantName - Name of the variant
     * @param {Object} styleOverrides - Style overrides for the variant
     * @returns {string} Variant className string
     */
    static useVariant({
        //The name of the component to look for variants
        componentName,
        //default variant name is 'default'
        variant = 'default',
        //the user may provide style overrides
        styleOverrides = {}
    }) {
        const ssrVariants = useVariantContext();
        const [asyncVariants, setAsyncVariants] = useState(null);

        // Compute variant synchronously on every render (useMemo ensures it only recomputes when deps change)
        const variantClass = useMemo(() => {
            let variants = null;

            // 1. SSR context (HIGHEST PRIORITY for initial hydration)
            if (ssrVariants && ssrVariants[componentName]) {
                variants = ssrVariants[componentName];
            }
            // 2. Check async fetched variants (from CMS, takes precedence after initial render)
            else if (asyncVariants) {
                variants = asyncVariants;
            }
            // 3. Runtime cache (API updates / editor state)
            else if (this.#variantCache.has(componentName)) {
                variants = this.#variantCache.get(componentName);
            }
            // 4. Window cache (synced from editor for live preview)
            else if (typeof window !== 'undefined' && window.__VBECS_VARIANT_CACHE__?.[componentName]) {
                variants = window.__VBECS_VARIANT_CACHE__[componentName];
                // Import to local cache for consistency
                this.#variantCache.set(componentName, variants);
            }
            // 5. Vike pageContext (client-only hydration fallback)
            else if (typeof window !== 'undefined' && window.__VIKE_PAGECONTEXT__?.pageContext?.ssrVariants?.[componentName]) {
                variants = window.__VIKE_PAGECONTEXT__.pageContext.ssrVariants[componentName];
            }
            // 6. Manual SSR variants (legacy hydration)
            else if (typeof window !== 'undefined' && window.__SSR_VARIANTS__?.[componentName]) {
                variants = window.__SSR_VARIANTS__[componentName];
            }

            // 7. Synchronous File Fallback (eager loaded)
            if (!variants && (defaultVariantMap[componentName] || userVariantMap[componentName])) {
                 variants = deepMergeVariants(
                     defaultVariantMap[componentName] || {},
                     userVariantMap[componentName] || {}
                 );
            }

            let result = '';

            if (variants) {
                 // Handle list lookup synchronously to avoid flicker
                 if (variant && variant.includes(' ')) {
                     const keys = variant.split(' ').filter(Boolean);
                     const classes = keys.map(k => variants[k] || '').join(' ');
                     result = classes || variants.default || '';
                 } else {
                     result = variants[variant] || variants.default || '';
                 }
            }

            // Parse variant-data string into object if it contains encoded data
            // Otherwise keep it as a plain string for use with class names
            if (typeof result === 'string' && result.includes('variant-data-[')) {
                const parsed = TailwindParser.parse(result);
                // If parsing succeeded and returned data, use it
                if (parsed && Object.keys(parsed).length > 0) {
                    result = parsed;
                }
            }

            return result || '';
        }, [componentName, variant, ssrVariants, asyncVariants]);

        // Fetch async variants from CMS in background
        useEffect(() => {
            this.readVariants(componentName).then((variants) => {
                setAsyncVariants(variants);
            });

            const token = PubSub.subscribe('VARIANTS_UPDATED', () => {
                this.readVariants(componentName).then((variants) => {
                    setAsyncVariants(variants);
                });
            });

            return () => {
                PubSub.unsubscribe(token);
            };
        }, [componentName]);

        // Apply style overrides if we have an object
        if (typeof variantClass === 'object' && styleOverrides && Object.keys(styleOverrides).length > 0) {
            return { ...variantClass, ...styleOverrides };
        }

        // Return string as-is for plain Tailwind classes, or object if parsed
        return variantClass || ''
    }

    /**
     * Reads variants for a specific component
     * @param {string} componentName - Name of the component (e.g., "Button")
     * @returns {Promise<Object>} Component variants
     */
    static async readVariants(componentName) {
        // Check cache first
        if (this.#variantCache.has(componentName)) {
            return this.#variantCache.get(componentName);
        }

        // Check pending requests
        if (this.#pendingRequests.has(componentName)) {
            return this.#pendingRequests.get(componentName);
        }

        const fetchPromise = (async () => {
            try {
                // Get default variants for the component (eager load)
                let defaultComponentVariants = defaultVariantMap[componentName] || {};

                // Get user-defined variants for the component (eager load)
                let userComponentVariants = userVariantMap[componentName] || {};

                // 1. Merge File Variants
                let mergedVariants = deepMergeVariants(
                    defaultComponentVariants,
                    userComponentVariants
                );

                // 2. Fetch & Merge CMS Variants
                // ... (rest of CMS fetching logic)
                try {
                    const res = await GetCMSContent({
                        match: {
                            'content.type': 'StyleVariant',
                            'content.component': componentName
                        },
                        itemsPerPage: 1000
                    });
                    if (res && res.data) {
                        const componentStyleVariants = res.data;
                        const cmsVariants = {};
                        componentStyleVariants.forEach(sv => {
                            if (sv.content?.styles && Array.isArray(sv.content.styles)) {
                                sv.content.styles.forEach(s => {
                                    if (s.key && s.value) {
                                        cmsVariants[s.key] = s.value;
                                    }
                                });
                            } else if (sv.content?.styles && typeof sv.content.styles === 'object') {
                                 Object.entries(sv.content.styles).forEach(([key, value]) => {
                                     cmsVariants[key] = value;
                                 });
                            }
                        });
                        mergedVariants = { ...mergedVariants, ...cmsVariants };
                    }
                } catch (err) {
                    console.warn("Failed to load CMS variants", err);
                }

                // Note: admin_ prefix stripping removed - admin.css now loaded separately
                // All variants are available regardless of pathname

                this.#variantCache.set(componentName, mergedVariants);
                return mergedVariants;
            } catch (error) {
                console.error(`Error loading variants for ${componentName}:`, error);
                return {};
            } finally {
                this.#pendingRequests.delete(componentName);
            }
        })();

        this.#pendingRequests.set(componentName, fetchPromise);
        return fetchPromise;
    }

    /**
     * Gets a specific variant class string (with state prefixes intact)
     * @param {string} componentName - Name of the component
     * @param {string} variantName - Name of the variant
     * @returns {Promise<string>} Variant class string
     */
    static async getVariantClassString(componentName, variantName = 'default') {
        const variants = await this.readVariants(componentName);

        let result = '';

        // Handle variant list (space separated)
        if (variantName && variantName.includes(' ')) {
            const keys = variantName.split(' ').filter(Boolean);
            const classes = keys.map(k => variants[k] || '').join(' ');
            result = classes || variants.default || '';
        } else {
            result = variants[variantName] || variants.default || '';
        }

        return result || '';
    }

    /**
     * Gets a specific variant for a component
     * @param {string} componentName - Name of the component
     * @param {string} variantName - Name of the variant
     * @returns {Promise<Object>} Variant properties object
     */
    static async getVariant(componentName, variantName = 'default') {
        const classString = await this.getVariantClassString(componentName, variantName);

        // Parse variant-data string into object if it contains encoded data
        // Otherwise return the plain string
        if (typeof classString === 'string' && classString.includes('variant-data-[')) {
            const parsed = TailwindParser.parse(classString);
            if (parsed && Object.keys(parsed).length > 0) {
                return parsed;
            }
        }

        // Return string as-is or empty string if nothing found
        return classString || '';
    }

    /**
     * Clears the variant cache
     */
    static clearCache() {
        this.#variantCache.clear();
    }

    /**
     * Updates the cache with new variant data (for immediate preview)
     * @param {string} componentName - Name of the component
     * @param {Object} variants - Variant data to cache
     */
    static updateCache(componentName, variants) {
        // Merge new variants with existing cache
        const existing = this.#variantCache.get(componentName) || {};
        const merged = { ...existing, ...variants };
        this.#variantCache.set(componentName, merged);
    }

    /**
     * Refreshes variants for a component by notifying subscribers without clearing cache
     * @param {string} componentName
     */
    static refresh(componentName) {
        PubSub.publish('VARIANTS_UPDATED', componentName);
    }

    /**
     * Exports the current variant cache for SSR or live preview
     * @returns {Object} Current variant cache as plain object
     */
    static exportCache() {
        const cacheObj = {};
        this.#variantCache.forEach((value, key) => {
            cacheObj[key] = value;
        });
        return cacheObj;
    }

    /**
     * Imports variant cache (used for hydration or editor sync)
     * @param {Object} cacheData - Variant cache data
     */
    static importCache(cacheData) {
        if (!cacheData || typeof cacheData !== 'object') return;
        Object.entries(cacheData).forEach(([componentName, variants]) => {
            this.#variantCache.set(componentName, variants);
        });
    }

    /**
     * Syncs variant cache to window context for live preview
     * This allows the live renderer to access editor's variant state
     */
    static syncToWindow() {
        if (typeof window !== 'undefined') {
            window.__VBECS_VARIANT_CACHE__ = this.exportCache();
        }
    }

    /**
     * Refreshes variants for a component by clearing cache and notifying subscribers
     * Use this after saving to reload from CMS
     * @param {string} componentName
     */
    static refreshFromSource(componentName) {
        this.#variantCache.delete(componentName);
        PubSub.publish('VARIANTS_UPDATED', componentName);
    }
}

export default VariantReader;
export { VariantProvider, useVariantContext } from './VariantContext.jsx';